export interface OAuthProviderFrontendConfig {
  name: string;
  displayName: string;
  description: string;
  icon?: string;
  color?: string;
  
  // Custom fields configuration for the dialog
  customFields?: Record<string, {
    label: string;
    placeholder?: string;
    required: boolean;
    type: 'text' | 'select';
    options?: string[] | Array<{ label: string; value: string }>;
    description: string;
    validation?: {
      pattern?: string;
      minLength?: number;
      maxLength?: number;
    };
  }>;
  
  // Help and documentation
  setupGuide?: string;
  oauthDocsUrl?: string;
  helpText?: string;
}

export const OAUTH_FRONTEND_CONFIGS: Record<string, OAuthProviderFrontendConfig> = {
  servicenow: {
    name: 'servicenow',
    displayName: 'ServiceNow',
    description: 'Connect to your ServiceNow instance for incident and task management',
    icon: '🔧',
    color: '#81C784',
    customFields: {
      instance: {
        label: 'ServiceNow Instance',
        placeholder: 'dev12345',
        required: true,
        type: 'text',
        description: 'Your ServiceNow instance name without .service-now.com',
        validation: {
          pattern: '^[a-zA-Z0-9-]+$',
          minLength: 3,
          maxLength: 50
        }
      }
    },
    setupGuide: 'https://docs.servicenow.com/bundle/vancouver-platform-administration/page/administer/security/task/t_SettingUpOAuth.html',
    oauthDocsUrl: 'https://developer.servicenow.com/dev.do#!/reference/api/vancouver/rest/c_OAuthAPI',
    helpText: 'To connect ServiceNow, you need to create an OAuth application in your ServiceNow instance and provide the Client ID and Client Secret.'
  },
  
  salesforce: {
    name: 'salesforce',
    displayName: 'Salesforce',
    description: 'Connect to Salesforce CRM for leads, opportunities, and customer data',
    icon: '☁️',
    color: '#1976D2',
    customFields: {
      domain: {
        label: 'Salesforce Domain',
        placeholder: 'login',
        required: true,
        type: 'text',
        description: 'Enter your Salesforce domain (e.g., "login" for production, "test" for sandbox, or your custom domain)'
      }
    },
    setupGuide: 'https://help.salesforce.com/s/articleView?id=sf.connected_app_create.htm',
    oauthDocsUrl: 'https://developer.salesforce.com/docs/atlas.en-us.api_rest.meta/api_rest/intro_understanding_oauth_endpoints.htm',
    helpText: 'Create a Connected App in Salesforce Setup to get your Client ID and Client Secret. Use "login" domain for production and "test" for sandbox.'
  },
  
  zoho_crm: {
    name: 'zoho_crm',
    displayName: 'Zoho CRM',
    description: 'Connect to Zoho CRM for customer relationship management',
    icon: '📊',
    color: '#FF7043',
    customFields: {
      region: {
        label: 'Zoho Region',
        required: true,
        type: 'select',
        options: [
          { label: 'United States (US)', value: 'https://accounts.zoho.com' },
          { label: 'Europe (EU)', value: 'https://accounts.zoho.eu' },
          { label: 'India (IN)', value: 'https://accounts.zoho.in' },
          { label: 'Australia (AU)', value: 'https://accounts.zoho.com.au' },
          { label: 'Japan (JP)', value: 'https://accounts.zoho.jp' },
          { label: 'Canada (CA)', value: 'https://accounts.zohocloud.ca' },
          { label: 'Saudi Arabia (SA)', value: 'https://accounts.zoho.sa' },
          { label: 'United Kingdom (UK)', value: 'https://accounts.zoho.uk' }
        ],
        description: 'Select your Zoho data center region'
      }
    },
    setupGuide: 'https://www.zoho.com/crm/developer/docs/api/v2/oauth-overview.html',
    oauthDocsUrl: 'https://www.zoho.com/crm/developer/docs/api/v2/oauth-overview.html',
    helpText: 'Register your application in Zoho Developer Console to obtain Client ID and Client Secret. Choose the correct region for your Zoho account.'
  },
  
  zendesk: {
    name: 'zendesk',
    displayName: 'Zendesk',
    description: 'Connect to Zendesk for customer support and ticketing',
    icon: '🎫',
    color: '#4CAF50',
    customFields: {
      subdomain: {
        label: 'Zendesk Subdomain',
        placeholder: 'your-company',
        required: true,
        type: 'text',
        description: 'Your Zendesk subdomain (e.g., "company" from company.zendesk.com)',
        validation: {
          pattern: '^[a-zA-Z0-9-]+$',
          minLength: 2,
          maxLength: 50
        }
      }
    },
    setupGuide: 'https://support.zendesk.com/hc/en-us/articles/4408845965210-Using-OAuth-authentication-with-your-application',
    oauthDocsUrl: 'https://developer.zendesk.com/api-reference/ticketing/introduction/#oauth-api',
    helpText: 'Create an OAuth application in your Zendesk Admin Center to get Client ID and Client Secret. Your subdomain is the part before .zendesk.com in your URL.'
  },
  
  
  hubspot: {
    name: 'hubspot',
    displayName: 'HubSpot',
    description: 'Connect to HubSpot CRM for marketing, sales, and customer service',
    icon: '🧡',
    color: '#FF7A59',
    customFields: {
      appid: {
        label: 'HubSpot App ID',
        placeholder: 'your-app-id',
        required: true,
        type: 'text',
        description: 'Your HubSpot App ID (found in your HubSpot app settings)',
        validation: {
          pattern: '^[a-zA-Z0-9-]+$',
          minLength: 1,
          maxLength: 100
        }
      }
    },
    setupGuide: 'https://developers.hubspot.com/docs/api/working-with-oauth',
    oauthDocsUrl: 'https://developers.hubspot.com/docs/api/oauth-quickstart-guide',
    helpText: 'Create a private app or OAuth app in your HubSpot Developer account to get Client ID and Client Secret. Your App ID can be found in your HubSpot app settings.'
  }
};

export interface OAuthConnectionData {
  provider: string;
  clientId: string;
  clientSecret: string;
  customParams: Record<string, string>;
}

export default OAUTH_FRONTEND_CONFIGS;
